import { Capacitor } from '@capacitor/core'
import { useEffect } from 'react'
import { haptic } from '../lib/native'

const interactiveSelector = [
  'button',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="reset"]',
  'input[type="image"]',
  'input[type="file"]',
  'a[href]',
  '[role="button"]',
  '[role="tab"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="menuitem"]',
  'summary',
].join(',')

let subscriberCount = 0
let removeListeners: (() => void) | null = null

/**
 * Supply native feedback for controls across the app, including portals and
 * dialogs. Explicit action feedback takes priority over the generic tap.
 */
export function useNativeInteractionFeedback() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    subscriberCount += 1
    if (subscriberCount === 1) {
      const click = (event: MouseEvent) => {
        const target = event.target
        if (!(target instanceof Element)) return
        const control = target.closest<HTMLElement>(interactiveSelector)
        if (
          !control ||
          control.matches(':disabled, [aria-disabled="true"]') ||
          control.dataset.haptic === 'none'
        ) {
          return
        }
        // Let an explicit handler request its semantic haptic first.
        queueMicrotask(() => {
          void haptic(control.dataset.haptic === 'medium' ? 'medium' : 'selection')
        })
      }

      const rangeInput = (event: Event) => {
        const target = event.target
        if (target instanceof HTMLInputElement && target.type === 'range') {
          void haptic('selection')
        }
      }

      document.addEventListener('click', click, true)
      document.addEventListener('input', rangeInput, true)
      removeListeners = () => {
        document.removeEventListener('click', click, true)
        document.removeEventListener('input', rangeInput, true)
      }
    }

    return () => {
      subscriberCount -= 1
      if (subscriberCount === 0) {
        removeListeners?.()
        removeListeners = null
      }
    }
  }, [])
}
